import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import { keyframes } from 'typestyle';

// Two themes, matching the approved HTML mockup (whyatwork-roomdisplay.html) exactly:
// the default dark theme and an "e-ink" high-contrast black/white serif variant toggled
// by the top-left button. All color tokens below are the mockup's :root / body.eink values.
export type Theme = 'dark' | 'eink';

const palette = {
  dark: {
    bg: '#0f172a',
    panel: '#1e293b',
    ink: '#f8fafc',
    muted: '#94a3b8',
    free: '#16a34a',
    busy: '#dc2626',
    soon: '#f59e0b',
    orange: '#f59e0b',
    blue: '#3b82f6',
    brand: '#4566b7',
    border: 'rgba(255,255,255,.08)',
  },
  eink: {
    bg: '#ffffff',
    panel: '#ffffff',
    ink: '#000000',
    muted: '#444444',
    free: '#000000',
    busy: '#000000',
    soon: '#000000',
    orange: '#000000',
    blue: '#000000',
    brand: '#000000',
    border: '#000000',
  },
};

export const colors = palette;

const systemFont = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif";
const einkFont = "Georgia,'Times New Roman',serif";
const fontFor = (theme: Theme): string => (theme === 'eink' ? einkFont : systemFont);

const pulse = keyframes({
  '0%': { boxShadow: '0 0 0 0 rgba(22,163,74,.6)' },
  '70%': { boxShadow: '0 0 0 12px rgba(22,163,74,0)' },
  '100%': { boxShadow: '0 0 0 0 rgba(22,163,74,0)' },
});

export type StatusState = 'free' | 'busy' | 'soon';

// === FRAME ===
// Wide screens keep the mockup's fixed-kiosk layout (full-height grid, schedule fills the
// bottom and scrolls internally). Below 900px the whole frame becomes a normal scrollable
// column so the stacked header + now-block + schedule can grow past the viewport height.
export const frame = (theme: Theme): NestedCSSProperties => ({
  height: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateRows: 'auto auto 1fr',
  padding: 18,
  gap: 14,
  background: palette[theme].bg,
  color: palette[theme].ink,
  fontFamily: fontFor(theme),
  overflow: 'hidden',
  $nest: {
    '@media (max-width: 900px)': {
      height: 'auto',
      minHeight: '100vh',
      gridTemplateRows: 'auto auto auto',
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: 12,
      gap: 12,
    },
  },
});

// === HEADER ===
export const head: NestedCSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 16,
  $nest: {
    '@media (max-width: 900px)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 10,
    },
  },
};

export const room: CSSProperties = {
  fontSize: 'clamp(20px, 2.6vw, 32px)',
  fontWeight: 700,
  letterSpacing: 0.5,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  textAlign: 'center',
};

// A live calendar-icon replacing the static 📅 emoji: white page with a colored month header
// strip and today's day number, so it always reflects the current date.
export const calIcon = (theme: Theme): CSSProperties => ({
  display: 'inline-flex',
  flexDirection: 'column',
  width: 42,
  height: 46,
  borderRadius: 8,
  overflow: 'hidden',
  background: '#fff',
  border: theme === 'eink' ? '2px solid #000' : `1px solid ${palette[theme].border}`,
  flex: '0 0 42px',
  lineHeight: 1,
});

export const calIconMonth = (theme: Theme): CSSProperties => ({
  background: theme === 'eink' ? '#000' : palette.dark.busy,
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.5,
  textAlign: 'center',
  padding: '3px 0',
});

export const calIconDay = (theme: Theme): CSSProperties => ({
  color: '#0f172a',
  fontSize: 22,
  fontWeight: 800,
  textAlign: 'center',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: fontFor(theme),
});

export const bookCta = (theme: Theme): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  background: palette[theme].panel,
  border: `1px solid ${palette[theme].border}`,
  borderRadius: 14,
  padding: '10px 16px',
  justifySelf: 'center',
});

// The QR code that used to sit beside this label was removed, so the text is now
// the only child of `bookCta` and centres rather than right-aligning against it.
export const ctaLabel: CSSProperties = {
  textAlign: 'center',
};

export const ctaT: CSSProperties = {
  fontWeight: 700,
  fontSize: 15,
  lineHeight: 1.1,
};

export const clockWrap: NestedCSSProperties = {
  textAlign: 'right',
  justifySelf: 'end',
  $nest: {
    '@media (max-width: 900px)': { textAlign: 'center', justifySelf: 'center' },
  },
};

export const clock: CSSProperties = {
  fontSize: 'clamp(24px, 2.8vw, 34px)',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
};

export const subdate = (theme: Theme): CSSProperties => ({
  fontSize: 13,
  color: palette[theme].muted,
  marginTop: 2,
});

export const refresh = (theme: Theme): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: palette[theme].muted,
  fontSize: 11,
  marginTop: 4,
});

export const dot = (theme: Theme): CSSProperties => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: theme === 'eink' ? '#000' : palette[theme].free,
  ...(theme === 'eink'
    ? {}
    : { animationName: pulse, animationDuration: '2s', animationIterationCount: 'infinite' }),
});

// === NOW / NEXT ===
export const now: NestedCSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14,
  alignItems: 'stretch',
  $nest: {
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
};

// The border tracks the status colour used by `statusBig` below (green free / red busy /
// orange soon), so the card reads as the status at a glance from across the room — the
// word itself is unreadable at that distance, the outline isn't. E-ink keeps its plain
// black rule, having no colour to carry the distinction.
export const statusCard = (theme: Theme, state: StatusState): NestedCSSProperties => ({
  background: palette[theme].panel,
  border: theme === 'eink' ? '3px solid #000' : `1px solid ${palette[theme][state]}`,
  borderRadius: 18,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 6,
  position: 'relative',
  overflow: 'hidden',
  $nest: {
    '@media (max-width: 600px)': { padding: 14 },
  },
});

export const statusLabel = (theme: Theme): CSSProperties => ({
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: palette[theme].muted,
});

export const statusBig = (theme: Theme, state: StatusState): CSSProperties => ({
  fontSize: 'clamp(44px, 9vw, 80px)',
  fontWeight: 800,
  lineHeight: 0.95,
  color: theme === 'eink' ? '#000' : palette[theme][state],
  letterSpacing: theme === 'eink' ? 1 : undefined,
});

export const statusSub = (theme: Theme): CSSProperties => ({
  fontSize: 'clamp(14px, 1.6vw, 18px)',
  color: palette[theme].muted,
});

export const nextCard = (theme: Theme): CSSProperties => ({
  background: palette[theme].panel,
  border: `1px solid ${palette[theme].border}`,
  borderRadius: 18,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

export const nextH3 = (theme: Theme): CSSProperties => ({
  margin: 0,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: palette[theme].muted,
  fontWeight: 700,
});

export const book = (theme: Theme): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: 8,
  borderRadius: 12,
  background: theme === 'eink' ? '#fff' : 'rgba(255,255,255,.04)',
  border: theme === 'eink' ? '1px dashed #000' : undefined,
});

export const bookEmpty = (theme: Theme): CSSProperties => ({
  display: 'flex',
  justifyContent: 'center',
  color: palette[theme].muted,
  fontStyle: 'italic',
  padding: 14,
  borderRadius: 12,
  background: theme === 'eink' ? '#fff' : 'rgba(255,255,255,.04)',
  border: theme === 'eink' ? '1px dashed #000' : undefined,
});

export const avatar = (theme: Theme): CSSProperties => ({
  width: 42,
  height: 42,
  borderRadius: '50%',
  background: theme === 'eink' ? '#fff' : palette[theme].brand,
  color: theme === 'eink' ? '#000' : '#fff',
  border: theme === 'eink' ? '2px solid #000' : undefined,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 15,
  flex: '0 0 42px',
  overflow: 'hidden',
});

export const bookWho: CSSProperties = {
  fontWeight: 700,
  fontSize: 16,
};

export const bookWhen = (theme: Theme): CSSProperties => ({
  fontSize: 13,
  color: palette[theme].muted,
});

export const bookOrg = (theme: Theme): CSSProperties => ({
  fontSize: 12,
  color: palette[theme].muted,
  fontStyle: 'italic',
});

// === SCHEDULE ===
export const schedule = (theme: Theme): NestedCSSProperties => ({
  background: palette[theme].panel,
  border: `1px solid ${palette[theme].border}`,
  borderRadius: 18,
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  // minWidth:0 lets this grid item shrink below its content's intrinsic width, so the week
  // view's fixed 700px grid scrolls inside weekScroll instead of widening the whole page.
  minWidth: 0,
  $nest: {
    // On the stacked/scrolling layout the schedule no longer gets the grid's `1fr`, so give it
    // an explicit workable height instead of collapsing.
    '@media (max-width: 900px)': { minHeight: 460, padding: '12px 12px' },
  },
});

export const schedHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 10,
};

export const schedTitle: CSSProperties = {
  fontWeight: 700,
  fontSize: 16,
  flex: 1,
};

export const viewSwitch = (theme: Theme): CSSProperties => ({
  display: 'inline-flex',
  background: theme === 'eink' ? '#fff' : 'rgba(255,255,255,.06)',
  border: theme === 'eink' ? '1px solid #000' : undefined,
  borderRadius: 8,
  padding: 3,
});

const activeBackground = (theme: Theme): string => (theme === 'eink' ? '#000' : palette[theme].brand);

export const viewSwitchButton = (theme: Theme, active: boolean): CSSProperties => ({
  border: 0,
  background: active ? activeBackground(theme) : 'transparent',
  color: active ? '#fff' : palette[theme].ink,
  padding: '6px 14px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontFor(theme),
});

export const schedBody: CSSProperties = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative',
};

// === DAY VIEW ===
export const dayScroll: CSSProperties = {
  height: '100%',
  minWidth: 0,
  overflow: 'auto',
};

export const day: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '50px 1fr',
};

export const hours: CSSProperties = {
  display: 'grid',
};

// `withLine` draws the horizontal hour rule. Day view keeps them; week view suppresses them so
// the only horizontal rule left is the one under the weekday header (see `colHead`).
export const hour = (theme: Theme, withLine = true): CSSProperties => ({
  height: 60,
  fontSize: 11,
  color: palette[theme].muted,
  textAlign: 'right',
  paddingRight: 8,
  borderTop: withLine ? `1px solid ${palette[theme].border}` : undefined,
});

export const grid = (theme: Theme): CSSProperties => ({
  position: 'relative',
  borderLeft: `1px solid ${palette[theme].border}`,
});

export const gridRow = (theme: Theme, withLine = true): CSSProperties => ({
  height: 60,
  borderTop: withLine ? `1px solid ${palette[theme].border}` : undefined,
});

// Base event card — mockup .ev (orange). A public kiosk has no signed-in viewer, so the
// mockup's "mine vs theirs" (orange vs blue) distinction has no meaning here; every event
// uses the mockup's default orange .ev. In e-ink it becomes the white/black bordered card.
//
// `isActive` marks the reservation currently in progress (facility status Busy). It takes the
// brand blue, which separates it from both the orange of the other bookings and the red of the
// now-line drawn across it. E-ink has no colour to spend, so it inverts to black-on-white
// instead — still the strongest contrast on the grid.
const eventBackground = (theme: Theme, isActive: boolean): string => {
  if (theme !== 'eink') {
    return palette[theme][isActive ? 'brand' : 'orange'];
  }
  return isActive ? '#000' : '#fff';
};

const eventForeground = (theme: Theme, isActive: boolean): string => (theme === 'eink' && !isActive ? '#000' : '#fff');

export const ev = (theme: Theme, isActive = false): CSSProperties => ({
  position: 'absolute',
  left: 6,
  right: 6,
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 13,
  color: eventForeground(theme, isActive),
  overflow: 'hidden',
  background: eventBackground(theme, isActive),
  border: theme === 'eink' ? '2px solid #000' : undefined,
  // Lifted above its neighbours so a back-to-back booking can't clip the in-progress one.
  zIndex: isActive ? 2 : 1,
});

export const evTitle: CSSProperties = {
  fontWeight: 700,
};

export const evSub: CSSProperties = {
  opacity: 0.92,
  fontSize: 12,
};

export const evWho: CSSProperties = {
  fontSize: 12,
  opacity: 0.92,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 2,
};

export const evMini = (theme: Theme): CSSProperties => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: theme === 'eink' ? '#fff' : 'rgba(255,255,255,.35)',
  color: theme === 'eink' ? '#000' : undefined,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 700,
  border: theme === 'eink' ? '1px solid #000' : '1px solid rgba(255,255,255,.6)',
});

// Red, against the brand-blue in-progress card it usually sits on top of — the two colours
// are deliberately different so the marker stays legible where they overlap.
export const nowLine = (theme: Theme): CSSProperties => ({
  position: 'absolute',
  left: 0,
  right: 0,
  height: 2,
  background: theme === 'eink' ? '#000' : palette[theme].busy,
  zIndex: 3,
});

export const nowLineDot = (theme: Theme): CSSProperties => ({
  position: 'absolute',
  left: -5,
  top: -4,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: theme === 'eink' ? '#000' : palette[theme].busy,
});

// === WEEK VIEW ===
export const weekScroll: CSSProperties = {
  height: '100%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
};

// minWidth keeps the 8 columns readable; below it the weekScroll container scrolls horizontally
// rather than crushing the day columns to unusable widths.
export const weekHeadGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '50px repeat(7, 1fr)',
  minWidth: 700,
};

export const weekBodyGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '50px repeat(7, 1fr)',
  flex: 1,
  minWidth: 700,
};

export const colHead = (theme: Theme, today: boolean): CSSProperties => ({
  padding: '6px 0',
  textAlign: 'center',
  fontSize: 12,
  color: today ? palette[theme].ink : palette[theme].muted,
  fontWeight: today ? 700 : 400,
  borderBottom: `1px solid ${palette[theme].border}`,
  borderLeft: `1px solid ${palette[theme].border}`,
});

export const colHeadNum = (theme: Theme, today: boolean): CSSProperties => {
  if (!today) {
    return { fontSize: 18, color: palette[theme].ink, fontWeight: 700 };
  }
  return {
    fontSize: 18,
    fontWeight: 700,
    display: 'inline-block',
    background: theme === 'eink' ? '#000' : palette[theme].brand,
    color: '#fff',
    width: 28,
    height: 28,
    lineHeight: '28px',
    borderRadius: '50%',
  };
};

export const dayCol = (theme: Theme): CSSProperties => ({
  borderLeft: `1px solid ${palette[theme].border}`,
  position: 'relative',
});

// === MONTH VIEW ===
export const month: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  height: '100%',
  gap: 2,
};

export const monthHeadRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 2,
  marginBottom: 4,
};

export const monthHeadCell = (theme: Theme): CSSProperties => ({
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: palette[theme].muted,
});

export const monthCell = (theme: Theme, other: boolean): CSSProperties => ({
  border: `1px solid ${palette[theme].border}`,
  borderRadius: 6,
  padding: 4,
  fontSize: 11,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  background: theme === 'eink' ? '#fff' : 'rgba(255,255,255,.02)',
  opacity: other ? 0.35 : 1,
});

export const monthNum = (theme: Theme, today: boolean): CSSProperties => {
  if (!today) {
    return { fontWeight: 700, color: palette[theme].ink, fontSize: 12 };
  }
  return {
    fontWeight: 700,
    fontSize: 12,
    background: theme === 'eink' ? '#000' : palette[theme].brand,
    color: '#fff',
    width: 22,
    height: 22,
    lineHeight: '22px',
    textAlign: 'center',
    borderRadius: '50%',
  };
};

// `isActive` mirrors `ev` above — the in-progress reservation turns red (black in e-ink).
export const monthPill = (theme: Theme, isActive = false): CSSProperties => ({
  background: eventBackground(theme, isActive),
  color: eventForeground(theme, isActive),
  border: theme === 'eink' ? '1px solid #000' : undefined,
  borderRadius: 3,
  padding: '1px 4px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 10,
});

// === THEME TOGGLE (fixed, top-left) ===
export const themeToggle = (theme: Theme): NestedCSSProperties => ({
  position: 'fixed',
  top: 6,
  left: 6,
  background: theme === 'eink' ? '#fff' : 'rgba(0,0,0,.4)',
  border: theme === 'eink' ? '1px solid #000' : `1px solid ${palette[theme].border}`,
  color: theme === 'eink' ? '#000' : palette[theme].ink,
  fontSize: 10,
  padding: '3px 7px',
  borderRadius: 6,
  cursor: 'pointer',
  opacity: 0.4,
  zIndex: 10,
  $nest: {
    '&:hover': { opacity: 1 },
  },
});

// === PAIRING / LOADING / ERROR / PAUSED ===
export const centeredMessage: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  gap: 16,
  textAlign: 'center',
};

export const pairingCode: CSSProperties = {
  fontSize: 64,
  fontWeight: 800,
  letterSpacing: 8,
};
